import React from 'react';
import { Clock, AlertCircle, Shield, MessageSquare, TrendingDown, Zap } from 'lucide-react';
import { Pattern } from '../../types/scan';
interface ShareableResultCardProps {
  score: number;
  riskLevel: 'low' | 'medium' | 'high';
  messageCount?: number;
  topPatterns?: Pattern[];
  variant?: 'square' | 'vertical';
  summary?: string;
  theme?: 'dark' | 'light';
}
export function ShareableResultCard({
  score,
  riskLevel = 'low',
  messageCount = 0,
  topPatterns = [],
  variant = 'square',
  summary,
  theme: cardTheme = 'dark'
}: ShareableResultCardProps) {
  const isVertical = variant === 'vertical';
  const isLight = cardTheme === 'light';
  // Theme Configuration based on risk level
  const themeConfig = {
    high: {
      color: '#EF4444',
      glowColor: 'rgba(239, 68, 68, 0.5)',
      textClass: 'text-red-500',
      bgClass: 'bg-red-500/10',
      borderClass: 'border-red-500/20',
      label: 'High Risk',
      verdict: 'TOXIC',
      summary: 'This conversation shows highly toxic communication patterns.',
      lightBadgeBg: '#FEF2F2',
      lightBadgeText: '#DC2626',
      lightBadgeBorder: '#FCA5A5'
    },
    medium: {
      color: '#F59E0B',
      glowColor: 'rgba(245, 158, 11, 0.5)',
      textClass: 'text-amber-500',
      bgClass: 'bg-amber-500/10',
      borderClass: 'border-amber-500/20',
      label: 'Medium Risk',
      verdict: 'CONCERNING',
      summary: 'This conversation shows some concerning communication patterns.',
      lightBadgeBg: '#FFFBEB',
      lightBadgeText: '#D97706',
      lightBadgeBorder: '#FCD34D'
    },
    low: {
      color: '#10B981',
      glowColor: 'rgba(16, 185, 129, 0.5)',
      textClass: 'text-emerald-500',
      bgClass: 'bg-emerald-500/10',
      borderClass: 'border-emerald-500/20',
      label: 'Low Risk',
      verdict: 'HEALTHY',
      summary: 'This conversation shows generally healthy communication patterns.',
      lightBadgeBg: '#ECFDF5',
      lightBadgeText: '#059669',
      lightBadgeBorder: '#6EE7B7'
    }
  };
  const theme = themeConfig[riskLevel] || themeConfig.low;
  // Helper to extract pattern info
  const getPatternInfo = (pattern: any) => {
    if (typeof pattern === 'string') {
      return {
        type: pattern,
        severity: 'MEDIUM' as const,
        description: ''
      };
    }
    return {
      type: pattern.type || 'Unknown',
      severity: pattern.severity || 'MEDIUM',
      description: pattern.description || ''
    };
  };
  // Helper functions
  const getPatternIcon = (type: string) => {
    if (!type) return Clock;
    const lower = type.toLowerCase();
    if (lower.includes('gaslight')) return AlertCircle;
    if (lower.includes('manipulat')) return Shield;
    if (lower.includes('dismiss')) return MessageSquare;
    if (lower.includes('blame')) return TrendingDown;
    if (lower.includes('aggress')) return Zap;
    return Clock;
  };
  const getSeverityColor = (severity: string) => {
    const s = severity?.toLowerCase();
    if (s === 'high') return '#EF4444';
    if (s === 'medium') return '#F59E0B';
    return '#10B981';
  };
  const getLightSeverityStyles = (severity: string) => {
    const s = severity?.toLowerCase();
    if (s === 'high') return {
      bg: '#FEF2F2',
      text: '#DC2626',
      border: '#FCA5A5'
    };
    if (s === 'medium') return {
      bg: '#FFFBEB',
      text: '#D97706',
      border: '#FCD34D'
    };
    return {
      bg: '#ECFDF5',
      text: '#059669',
      border: '#6EE7B7'
    };
  };
  const displayPatterns = topPatterns.slice(0, isVertical ? 4 : 3);
  const today = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const displaySummary = summary || theme.summary;
  // Common Background Grid
  const BackgroundGrid = () => <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-114">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)]" data-id="element-115" />
    </div>;
  if (isVertical) {
    // --- VERTICAL (STORY) VARIANT ---
    const radius = 160;
    const strokeWidth = 28;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - score / 100 * circumference;
    const ringSize = 400;
    if (isLight) {
      return <div className="w-[1080px] h-[1920px] bg-[#FAFAFA] flex flex-col font-sans text-slate-900 relative overflow-hidden" data-id="element-116">
          {/* Header */}
          <header className="px-16 pt-20 pb-8 relative z-10" data-id="element-117">
            <div className="flex items-center justify-between mb-6" data-id="element-118">
              <h1 className="text-3xl font-black tracking-tight text-slate-900" data-id="element-119">
                ToxicDetector.ai
              </h1>
              <span className="text-xl text-slate-500 font-medium" data-id="element-120">
                Chat Analysis Result
              </span>
            </div>
            <div className="w-full h-px bg-slate-200" data-id="element-121" />
          </header>

          {/* Main Content */}
          <main className="flex-1 flex flex-col items-center px-16 pt-12 relative z-10" data-id="element-122">
            {/* Score Ring */}
            <div className="relative flex items-center justify-center mb-12" style={{
            width: ringSize,
            height: ringSize
          }} data-id="element-123">
              <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} data-id="element-124">
                <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} data-id="element-125" />
                <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-126" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-127">
                <span className="text-[130px] font-black tracking-tighter leading-none text-slate-900" data-id="element-128">
                  {score}%
                </span>
              </div>
            </div>

            {/* Verdict */}
            <div className="flex flex-col items-center mb-12" data-id="element-129">
              <h2 className="text-7xl font-black tracking-tight mb-6 uppercase text-center text-slate-900" data-id="element-130">
                {score}% {theme.verdict}
              </h2>
              <div className="px-6 py-2 rounded-full border flex items-center gap-2" style={{
              backgroundColor: theme.lightBadgeBg,
              borderColor: theme.lightBadgeBorder
            }} data-id="element-131">
                <div className="w-2.5 h-2.5 rounded-full" style={{
                backgroundColor: theme.color
              }} data-id="element-132" />
                <span className="text-xl font-bold uppercase tracking-wide" style={{
                color: theme.lightBadgeText
              }} data-id="element-133">
                  {theme.label} Detected
                </span>
              </div>
            </div>

            {/* Summary */}
            <div className="text-center mb-4 max-w-3xl" data-id="element-134">
              <p className="text-3xl font-semibold text-slate-800 leading-relaxed" data-id="element-135">
                {displaySummary}
              </p>
            </div>

            <div className="text-center mb-16" data-id="element-136">
              <p className="text-xl text-slate-500 font-medium" data-id="element-137">
                Based on communication patterns, not intent.
              </p>
            </div>

            {/* Patterns List */}
            <div className="w-full max-w-4xl flex flex-col gap-6 mb-16" data-id="element-138">
              {displayPatterns.map((pattern, index) => {
              const info = getPatternInfo(pattern);
              const PatternIcon = getPatternIcon(info.type);
              const lightStyles = getLightSeverityStyles(info.severity);
              return <div key={index} className="bg-white rounded-3xl border border-slate-200 p-8 flex items-center justify-between shadow-sm" data-id="element-139">
                    <div className="flex items-center gap-5" data-id="element-140">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600" data-id="element-141">
                        <PatternIcon size={24} strokeWidth={2} data-id="element-142" />
                      </div>
                      <h3 className="text-3xl font-bold text-slate-900 capitalize" data-id="element-143">
                        {info.type}
                      </h3>
                    </div>
                    <span className="px-5 py-2 rounded-full text-sm font-bold uppercase tracking-widest border" style={{
                  backgroundColor: lightStyles.bg,
                  color: lightStyles.text,
                  borderColor: lightStyles.border
                }} data-id="element-144">
                      {info.severity}
                    </span>
                  </div>;
            })}

              {displayPatterns.length === 0 && <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm" data-id="element-145">
                  <p className="text-2xl font-medium text-slate-500" data-id="element-146">
                    No concerning patterns detected.
                  </p>
                </div>}
            </div>

            {/* Stats Row */}
            <div className="flex items-center justify-center gap-12 text-2xl font-bold text-slate-900 mb-12" data-id="element-147">
              <div className="flex flex-col items-start" data-id="element-148">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-149">
                  Messages:
                </span>
                <span className="text-xl" data-id="element-150">{messageCount}</span>
              </div>
              <div className="w-px h-12 bg-slate-200" data-id="element-151" />
              <div className="flex flex-col items-start" data-id="element-152">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-153">
                  Patterns Found: {topPatterns.length}
                </span>
              </div>
              <div className="w-px h-12 bg-slate-200" data-id="element-154" />
              <div className="flex flex-col items-start" data-id="element-155">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-156">
                  Risk Level:
                </span>
                <span className="text-xl capitalize" data-id="element-157">{riskLevel}</span>
              </div>
            </div>

            <div className="text-center mb-auto" data-id="element-158">
              <p className="text-2xl font-medium text-slate-700" data-id="element-159">
                Scan your conversations for unhealthy patterns
              </p>
            </div>

            {/* Brand Footer */}
            <div className="w-full max-w-4xl bg-[#F8FAFC] rounded-3xl p-10 flex flex-col items-center justify-center text-center mt-12 mb-16" data-id="element-160">
              <div className="flex items-center gap-4 mb-4" data-id="element-161">
                <span className="text-4xl" data-id="element-162">🚩</span>
                <span className="text-4xl font-black text-slate-900 tracking-tight" data-id="element-163">
                  Made with ToxicDetector.ai
                </span>
              </div>
              <span className="text-2xl text-slate-500 font-medium" data-id="element-164">
                AI-powered relationship analysis
              </span>
            </div>
          </main>
        </div>;
    }
    return <div className="w-[1080px] h-[1920px] bg-[#0A0A0A] flex flex-col font-sans text-white relative overflow-hidden" style={{
      '--theme-glow-color': theme.glowColor
    } as React.CSSProperties} data-id="element-165">
        <BackgroundGrid data-id="element-166" />

        {/* Subtle glow behind ring */}
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none" style={{
        backgroundColor: theme.color
      }} data-id="element-167" />

        {/* Header */}
        <header className="px-16 pt-20 pb-8 relative z-10" data-id="element-168">
          <div className="flex items-center justify-between mb-6" data-id="element-169">
            <div className="flex items-center gap-4" data-id="element-170">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-black font-black text-xl tracking-tighter" data-id="element-171">
                TD
              </div>
              <h1 className="text-3xl font-black tracking-tight text-white" data-id="element-172">
                ToxicDetector.ai
              </h1>
            </div>
            <span className="text-xl text-white/50 font-mono uppercase tracking-widest" data-id="element-173">
              Forensic Report
            </span>
          </div>
          <div className="w-full h-px bg-white/10" data-id="element-174" />
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center px-16 pt-12 relative z-10" data-id="element-175">
          {/* Score Ring */}
          <div className="relative flex items-center justify-center mb-12" style={{
          width: ringSize,
          height: ringSize
        }} data-id="element-176">
            <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} style={{
            filter: 'drop-shadow(0 0 20px var(--theme-glow-color))'
          }} data-id="element-177">
              <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={strokeWidth} data-id="element-178" />
              <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-179" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-180">
              <span className="text-[130px] font-black tracking-tighter leading-none" style={{
              color: theme.color
            }} data-id="element-181">
                {score}
              </span>
              <span className="text-2xl font-mono text-white/50 mt-2 uppercase tracking-widest" data-id="element-182">
                Score
              </span>
            </div>
          </div>

          {/* Verdict */}
          <div className="flex flex-col items-center mb-16" data-id="element-183">
            <h2 className="text-7xl font-black tracking-tight mb-4 uppercase text-center" style={{
            color: theme.color
          }} data-id="element-184">
              {theme.verdict}
            </h2>
            <div className={`px-6 py-2 rounded-full border ${theme.borderClass} ${theme.bgClass}`} data-id="element-185">
              <span className={`text-xl font-bold ${theme.textClass} uppercase tracking-widest`} data-id="element-186">
                {theme.label} Detected
              </span>
            </div>
          </div>

          {/* Summary */}
          <div className="text-center mb-16 max-w-3xl" data-id="element-187">
            <p className="text-3xl font-medium text-white/90 leading-relaxed" data-id="element-188">
              {displaySummary}
            </p>
          </div>

          {/* Patterns List */}
          <div className="w-full max-w-4xl flex flex-col gap-6 mb-16" data-id="element-189">
            <h3 className="text-2xl font-mono text-white/50 uppercase tracking-widest mb-2 pl-4" data-id="element-190">
              Detected Patterns
            </h3>
            {displayPatterns.map((pattern, index) => {
            const info = getPatternInfo(pattern);
            const PatternIcon = getPatternIcon(info.type);
            const sevColor = getSeverityColor(info.severity);
            return <div key={index} className="bg-white/5 rounded-3xl border border-white/10 p-8 flex flex-col gap-4 relative overflow-hidden" data-id="element-191">
                  {/* Severity Accent Line */}
                  <div className="absolute left-0 top-0 bottom-0 w-2" style={{
                backgroundColor: sevColor
              }} data-id="element-192" />

                  <div className="flex items-center justify-between pl-4" data-id="element-193">
                    <div className="flex items-center gap-5" data-id="element-194">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white" data-id="element-195">
                        <PatternIcon size={24} strokeWidth={2} data-id="element-196" />
                      </div>
                      <h3 className="text-3xl font-bold text-white capitalize" data-id="element-197">
                        {info.type}
                      </h3>
                    </div>
                    <span className="px-5 py-2 rounded-full text-sm font-mono font-bold uppercase tracking-widest" style={{
                  '--sev-color': sevColor,
                  '--sev-color-20': `${sevColor}20`,
                  '--sev-color-40': `${sevColor}40`,
                  backgroundColor: 'var(--sev-color-20)',
                  color: 'var(--sev-color)',
                  border: '1px solid var(--sev-color-40)'
                } as React.CSSProperties} data-id="element-198">
                      {info.severity}
                    </span>
                  </div>
                  {info.description && <p className="text-xl text-white/60 leading-relaxed pl-21 ml-[68px]" data-id="element-199">
                      {info.description}
                    </p>}
                </div>;
          })}

            {displayPatterns.length === 0 && <div className="bg-white/5 rounded-3xl border border-white/10 p-12 text-center" data-id="element-200">
                <p className="text-2xl font-medium text-white/50" data-id="element-201">
                  No concerning patterns detected.
                </p>
              </div>}
          </div>

          {/* Stats Row */}
          <div className="flex items-center justify-center gap-12 text-2xl font-mono text-white/60 mb-auto" data-id="element-202">
            <div className="flex flex-col items-center" data-id="element-203">
              <span className="text-white font-bold text-4xl mb-2" data-id="element-204">
                {messageCount}
              </span>
              <span className="text-sm uppercase tracking-widest" data-id="element-205">
                Messages
              </span>
            </div>
            <div className="w-px h-16 bg-white/10" data-id="element-206" />
            <div className="flex flex-col items-center" data-id="element-207">
              <span className="text-white font-bold text-4xl mb-2" data-id="element-208">
                {topPatterns.length}
              </span>
              <span className="text-sm uppercase tracking-widest" data-id="element-209">
                Patterns
              </span>
            </div>
          </div>

          {/* Brand Footer */}
          <div className="w-full max-w-4xl bg-white/5 border border-white/10 rounded-3xl p-10 flex flex-col items-center justify-center text-center mt-16 mb-16 relative overflow-hidden" data-id="element-210">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50" data-id="element-211" />
            <div className="flex items-center gap-4 mb-4" data-id="element-212">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-black font-black text-lg tracking-tighter" data-id="element-213">
                TD
              </div>
              <span className="text-3xl font-black text-white tracking-tight" data-id="element-214">
                ToxicDetector.ai
              </span>
            </div>
            <span className="text-xl text-white/50 font-mono uppercase tracking-widest" data-id="element-215">
              AI-Powered Relationship Analysis
            </span>
          </div>
        </main>
      </div>;
  }
  // --- SQUARE (1:1) VARIANT ---
  if (isLight) {
    const radius = 120;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - score / 100 * circumference;
    const ringSize = 300;
    return <div className="w-[1080px] h-[1080px] bg-[#FAFAFA] flex flex-col font-sans text-slate-900 relative overflow-hidden p-16" data-id="element-216">
        {/* Header */}
        <header className="flex items-center justify-between mb-12 relative z-10" data-id="element-217">
          <h1 className="text-4xl font-black tracking-tight text-slate-900" data-id="element-218">
            ToxicDetector.ai
          </h1>
          <div className="text-right" data-id="element-219">
            <div className="text-slate-500 font-medium text-xl" data-id="element-220">
              Chat Analysis Result
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col relative z-10" data-id="element-221">
          <div className="flex items-center justify-between mb-16" data-id="element-222">
            <div className="flex flex-col" data-id="element-223">
              <h2 className="text-3xl font-medium text-slate-500 mb-4" data-id="element-224">
                Analysis Result
              </h2>
              <span className="text-7xl font-black tracking-tight uppercase text-slate-900" data-id="element-225">
                {score}% {theme.verdict}
              </span>
              <div className="mt-6 flex items-center gap-4" data-id="element-226">
                <div className="px-6 py-2 rounded-full border flex items-center gap-2" style={{
                backgroundColor: theme.lightBadgeBg,
                borderColor: theme.lightBadgeBorder
              }} data-id="element-227">
                  <div className="w-2.5 h-2.5 rounded-full" style={{
                  backgroundColor: theme.color
                }} data-id="element-228" />
                  <span className="text-xl font-bold uppercase tracking-wide" style={{
                  color: theme.lightBadgeText
                }} data-id="element-229">
                    {theme.label}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end" data-id="element-230">
              <div className="relative flex items-center justify-center" style={{
              width: ringSize,
              height: ringSize
            }} data-id="element-231">
                <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} data-id="element-232">
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} data-id="element-233" />
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-234" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-235">
                  <span className="text-[80px] font-black tracking-tighter leading-none text-slate-900" data-id="element-236">
                    {score}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Patterns Box */}
          <div className="w-full flex-1 flex flex-col" data-id="element-237">
            <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-238">
              Detected Patterns ({topPatterns.length})
            </h3>

            <div className="grid grid-cols-1 gap-4" data-id="element-239">
              {displayPatterns.map((pattern, index) => {
              const info = getPatternInfo(pattern);
              const lightStyles = getLightSeverityStyles(info.severity);
              return <div key={index} className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-id="element-240">
                    <span className="text-3xl font-bold text-slate-900 capitalize pl-4" data-id="element-241">
                      {info.type}
                    </span>
                    <span className="px-5 py-2 rounded-full text-lg font-bold uppercase tracking-widest border" style={{
                  backgroundColor: lightStyles.bg,
                  color: lightStyles.text,
                  borderColor: lightStyles.border
                }} data-id="element-242">
                      {info.severity}
                    </span>
                  </div>;
            })}

              {displayPatterns.length === 0 && <div className="flex items-center justify-center bg-white border border-slate-200 rounded-2xl p-10 shadow-sm" data-id="element-243">
                  <span className="text-2xl font-medium text-slate-500" data-id="element-244">
                    No concerning patterns detected
                  </span>
                </div>}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-slate-200 flex items-center justify-between relative z-10" data-id="element-245">
          <div className="flex items-center gap-6 text-2xl font-medium text-slate-500" data-id="element-246">
            <span data-id="element-247">{messageCount} Messages Analyzed</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-248">
            <span className="text-3xl" data-id="element-249">🚩</span>
            <span className="text-2xl font-bold text-slate-900 tracking-wide" data-id="element-250">
              toxicdetector.ai
            </span>
          </div>
        </footer>
      </div>;
  }
  return <div className="w-[1080px] h-[1080px] bg-[#0A0A0A] flex flex-col font-sans text-white relative overflow-hidden p-16" data-id="element-251">
      <BackgroundGrid data-id="element-252" />

      {/* Subtle glow behind score */}
      <div className="absolute top-[30%] left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[150px] opacity-15 pointer-events-none" style={{
      backgroundColor: theme.color
    }} data-id="element-253" />

      {/* Header */}
      <header className="flex items-center justify-between mb-12 relative z-10" data-id="element-254">
        <div className="flex items-center gap-4" data-id="element-255">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-black font-black text-2xl tracking-tighter" data-id="element-256">
            TD
          </div>
          <h1 className="text-4xl font-black tracking-tight text-white" data-id="element-257">
            ToxicDetector.ai
          </h1>
        </div>
        <div className="text-right" data-id="element-258">
          <div className="text-white/50 font-mono text-lg uppercase tracking-widest mb-1" data-id="element-259">
            Date
          </div>
          <div className="text-white font-medium text-xl" data-id="element-260">{today}</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative z-10" data-id="element-261">
        <div className="flex items-center justify-between mb-16" data-id="element-262">
          <div className="flex flex-col" data-id="element-263">
            <h2 className="text-3xl font-mono text-white/50 uppercase tracking-widest mb-4" data-id="element-264">
              Forensic Analysis
            </h2>
            <span className="text-7xl font-black tracking-tight uppercase" style={{
            color: theme.color
          }} data-id="element-265">
              {theme.verdict}
            </span>
            <div className="mt-4 flex items-center gap-4" data-id="element-266">
              <span className="text-2xl text-white/70 font-medium" data-id="element-267">
                Risk Level:
              </span>
              <div className={`px-4 py-1.5 rounded-full border ${theme.borderClass} ${theme.bgClass} inline-block`} data-id="element-268">
                <span className={`text-lg font-bold ${theme.textClass} uppercase tracking-widest`} data-id="element-269">
                  {theme.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end" data-id="element-270">
            <span className="text-[180px] font-black leading-none tracking-tighter" style={{
            color: theme.color,
            textShadow: `0 0 40px ${theme.glowColor}`
          }} data-id="element-271">
              {score}
            </span>
            <span className="text-3xl font-mono text-white/50 uppercase tracking-widest mt-2" data-id="element-272">
              Toxicity Score
            </span>
          </div>
        </div>

        {/* Patterns Box */}
        <div className="w-full flex-1 flex flex-col" data-id="element-273">
          <h3 className="text-2xl font-mono text-white/50 uppercase tracking-widest mb-6" data-id="element-274">
            Detected Patterns ({topPatterns.length})
          </h3>

          <div className="grid grid-cols-1 gap-4" data-id="element-275">
            {displayPatterns.map((pattern, index) => {
            const info = getPatternInfo(pattern);
            const sevColor = getSeverityColor(info.severity);
            return <div key={index} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden" data-id="element-276">
                  <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{
                backgroundColor: sevColor
              }} data-id="element-277" />
                  <span className="text-3xl font-bold text-white capitalize pl-4" data-id="element-278">
                    {info.type}
                  </span>
                  <span className="px-5 py-2 rounded-full text-lg font-mono font-bold uppercase tracking-widest" style={{
                '--sev-color': sevColor,
                '--sev-color-20': `${sevColor}20`,
                '--sev-color-40': `${sevColor}40`,
                backgroundColor: 'var(--sev-color-20)',
                color: 'var(--sev-color)',
                border: '1px solid var(--sev-color-40)'
              } as React.CSSProperties} data-id="element-279">
                    {info.severity}
                  </span>
                </div>;
          })}

            {displayPatterns.length === 0 && <div className="flex items-center justify-center bg-white/5 border border-white/10 rounded-2xl p-10" data-id="element-280">
                <span className="text-2xl font-medium text-white/50" data-id="element-281">
                  No concerning patterns detected
                </span>
              </div>}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between relative z-10" data-id="element-282">
        <div className="flex items-center gap-6 text-2xl font-mono text-white/50" data-id="element-283">
          <span data-id="element-284">{messageCount} Messages Analyzed</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-285">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-286" />
          <span className="text-2xl font-bold text-white tracking-wide" data-id="element-287">
            toxicdetector.ai
          </span>
        </div>
      </footer>
    </div>;
}